import { type ReactElement, useState, type CSSProperties } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { type ViewModelId } from 'balus-common';
import { MCPBridgeButton } from '../FloatingUIContainer/MCPBridge/MCPBridgeButton';
import { MCPBridgeLogView } from '../FloatingUIContainer/MCPBridge/MCPBridgeLogView';
import { AIAssistantSettingsStorage as storage } from './AIAssistantSettingsStorage';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAIAssistantRouter } from './aiAssistantRouteAtom';

interface State {
  prompt: string;
  hasUnsavedPromptChanges: boolean;
}

export function AIAssistantSettings({ viewModelId }: { viewModelId: ViewModelId }): ReactElement {
  const { t } = useTranslation();
  const router = useAIAssistantRouter(viewModelId);
  const [state, setState] = useState<State>({
    prompt: storage.getCustomSystemPrompt(),
    hasUnsavedPromptChanges: false,
  });

  const handleSave = () => {
    try {
      storage.setCustomSystemPrompt(state.prompt);
      toast.success(t('viewModel.aiSettings.toastSaveSuccess'));
    } catch (e) {
      toast.error(t('common.message.saveSettingsFailed'));
      throw e;
    }
    setState((prev) => ({ ...prev, hasUnsavedPromptChanges: false }));
  };

  const hasUnsavedChanges = state.hasUnsavedPromptChanges;

  return (
    <div className="flex min-h-0 grow flex-col gap-4 overflow-y-auto">
      <div className="flex items-center gap-2 py-3">
        <button
          className="text-sm text-gray-700 hover:text-gray-900"
          title={t('viewModel.aiChat.backLabel')}
          onClick={() => {
            router.back();
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <h2 className="text-base font-bold">{t('viewModel.aiSettings.title')}</h2>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-gray-800">{t('viewModel.aiSettings.systemPromptLabel')}</div>
          <button
            className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50"
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
            type="button"
          >
            {t('viewModel.aiSettings.save')}
          </button>
        </div>
        <textarea
          className="mt-2 block min-h-[100px] w-full resize-none rounded border border-gray-300 p-1 text-sm"
          placeholder={t('viewModel.aiSettings.systemPromptPlaceholder')}
          style={
            {
              fieldSizing: 'content',
            } as CSSProperties
          }
          value={state.prompt}
          aria-label={t('viewModel.aiSettings.systemPromptLabel')}
          onChange={(e) => {
            const prompt = e.target.value;
            setState((prev) => ({ ...prev, prompt, hasUnsavedPromptChanges: true }));
          }}
        />
      </div>

      <div className="mt-2">
        <div className="text-sm font-semibold text-gray-800">{t('viewModel.aiSettings.mcpBridgeLabel')}</div>
        <div className="mt-2">
          <MCPBridgeButton />
        </div>
        <div className="mt-2">
          <MCPBridgeLogView />
        </div>
      </div>

      <div>
        <div className="text-sm font-semibold text-gray-800">{t('viewModel.aiSettings.debugLabel')}</div>
        <label className="mt-2 flex items-center">
          <input
            type="checkbox"
            className="form-checkbox mr-2"
            defaultChecked={storage.getDebugMessageEnabled()}
            onChange={(e) => {
              storage.setDebugMessageEnabled(e.target.checked);
            }}
          />
          <span className="text-sm text-gray-700">{t('viewModel.aiSettings.debugCheckboxLabel')}</span>
        </label>
      </div>
    </div>
  );
}
